import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export interface User {
  userId: string
  email: string
  userName: string
  userRole: 'ADMIN' | 'USER'
  createdTime: string
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        if (lumi.auth.isAuthenticated) {
          const currentUser = await lumi.auth.refreshUser()
          setUser(currentUser)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()

    // Listen to auth state changes
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user: authUser }) => {
      if (isAuthenticated && authUser) {
        setUser(authUser)
      } else {
        setUser(null)
      }
    })

    return () => unsubscribe?.()
  }, [])

  const signIn = async () => {
    try {
      const { user: loggedInUser } = await lumi.auth.signIn()
      setUser(loggedInUser)
      return loggedInUser
    } catch (error) {
      console.error('Sign in failed:', error)
      throw error
    }
  }

  const signOut = async () => {
    try {
      await lumi.auth.signOut()
      setUser(null)
    } catch (error) {
      console.error('Sign out failed:', error)
      throw error
    }
  }

  return {
    user,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!user
  }
}
